/**
 * speech.js — Iter82.
 *
 * Thin wrapper around the browser's Web Speech API for the Language
 * Learning module. Exposes:
 *   - speak(text, code)        → pronounce target-language text
 *   - cancelSpeak()            → stop any in-flight utterance
 *   - SUPPORTS_TTS / SUPPORTS_STT booleans
 *   - createRecognition(code)  → SpeechRecognition instance bound to lang
 *
 * Why not a separate audio service? Web Speech is free, on-device, and
 * works offline in modern Chromium/WebKit. Falling back to a server
 * TTS would burn LLM credits per word and add latency. Web Speech is
 * the right tool here and matches Duolingo's pronunciation polish.
 */

// BCP-47 mapping for our 12 supported languages (see backend
// routes/language.py LANGUAGES list). The browser picks the best
// installed voice for the locale; missing voices silently no-op.
const BCP47 = {
  en: "en-US", es: "es-ES", fr: "fr-FR", de: "de-DE",
  it: "it-IT", pt: "pt-PT", hi: "hi-IN", ja: "ja-JP",
  ko: "ko-KR", zh: "zh-CN", ar: "ar-SA", ru: "ru-RU",
};

export function toBcp47(code) {
  return BCP47[code] || code || "en-US";
}

export const SUPPORTS_TTS =
  typeof window !== "undefined" &&
  typeof window.speechSynthesis !== "undefined";

export const SUPPORTS_STT =
  typeof window !== "undefined" &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

let _voices = null;
function _loadVoices() {
  if (!SUPPORTS_TTS) return [];
  if (_voices && _voices.length) return _voices;
  _voices = window.speechSynthesis.getVoices() || [];
  return _voices;
}
if (SUPPORTS_TTS) {
  // Voices load async on first paint in some browsers — refresh on event.
  window.speechSynthesis.onvoiceschanged = () => {
    _voices = window.speechSynthesis.getVoices() || [];
  };
}

function _bestVoice(lang) {
  const voices = _loadVoices();
  if (!voices.length) return null;
  // Prefer an exact BCP-47 match, then any voice starting with the
  // language base ("es"), then null (let the browser pick a default).
  return (
    voices.find((v) => v.lang === lang) ||
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(lang.slice(0, 2))) ||
    null
  );
}

export function speak(text, code, opts = {}) {
  if (!SUPPORTS_TTS || !text) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.lang = toBcp47(code);
    const v = _bestVoice(u.lang);
    if (v) u.voice = v;
    u.rate = opts.rate ?? 0.92;   // slightly slower for learners
    u.pitch = opts.pitch ?? 1.0;
    u.volume = opts.volume ?? 1.0;
    window.speechSynthesis.speak(u);
  } catch (_) {
    /* swallow — speechSynthesis not available / interrupted */
  }
}

export function cancelSpeak() {
  if (!SUPPORTS_TTS) return;
  try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
}

export function createRecognition(code) {
  if (!SUPPORTS_STT) return null;
  const Ctor =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  const rec = new Ctor();
  rec.lang = toBcp47(code);
  rec.interimResults = false;
  rec.maxAlternatives = 3;
  rec.continuous = false;
  return rec;
}

// Loose normalisation used for STT answer matching — strip punctuation,
// collapse whitespace, fold case + diacritics. We're forgiving on purpose:
// browsers transcribe imperfectly and learners will too.
export function normaliseUtterance(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,!?¿¡;:"'`´()\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Word-level Levenshtein edit distance — used by the Listen & Repeat
// exercise to grade pronunciation more strictly than a blunt
// substring check. Operates on token arrays, not chars, because
// learners drop or duplicate whole words far more often than letters.
function _editDistance(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  // Two-row DP to keep memory O(min(m,n)).
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,         // insertion
        prev[j] + 1,             // deletion
        prev[j - 1] + cost,      // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/**
 * Returns a similarity score in [0, 1] comparing the spoken
 * `transcript` against the `expected` target-language sentence.
 *  - Both inputs are normalised + tokenised on whitespace.
 *  - Score = 1 - (wordEditDistance / maxWordCount).
 *  - Identical → 1.0;  totally different → 0.0.
 */
export function similarity(transcript, expected) {
  const a = normaliseUtterance(transcript).split(" ").filter(Boolean);
  const b = normaliseUtterance(expected).split(" ").filter(Boolean);
  if (a.length === 0 && b.length === 0) return 1;
  if (a.length === 0 || b.length === 0) return 0;
  const dist = _editDistance(a, b);
  const denom = Math.max(a.length, b.length);
  return Math.max(0, 1 - dist / denom);
}
